/**
 * Iskalni laboratorij (seja 384): izbire za preizkus iskanja po členih v živo na dev instanci.
 * SAMO metapodatki (varno za klienta); izvajanje je v lib/opensearch/search-overrides.ts in
 * lib/opensearch/client.ts. Vzorec CHAT_MODELS / RERANKERS: id → opis, razreševanje z varnim
 * privzetkom. Meritve: docs/teorija/VLOZITVENI_MODEL_V_HIBRIDU.md.
 *
 * Privzeto (»privzeto« pri vseh treh) se iskanje ne spremeni.
 */

export type LabEmbedModel = 'privzeto' | 'minilm' | 'arctic';
export type LabKnnWeight = 'privzeto' | '0.3' | '0.5' | '0.7' | '0.9';
export type LabFlash = 'privzeto' | 'zacetek' | 'kosc' | 'off';

export interface SearchLabChoice {
  embed: LabEmbedModel;
  weight: LabKnnWeight;
  flash: LabFlash;
}

export const SEARCH_LAB_DEFAULT: SearchLabChoice = { embed: 'privzeto', weight: 'privzeto', flash: 'privzeto' };

export const LAB_EMBED_OPTIONS: { id: LabEmbedModel; label: string; description: string }[] = [
  { id: 'privzeto', label: 'Privzeto', description: 'Model, ki je nastavljen na instanci (PISRS_ARTICLE_EMBED).' },
  { id: 'minilm', label: 'MiniLM', description: 'Produkcijski model, 384 dimenzij. Čisti vektor sidro@3 74,2 %; golden v4 R@5 53,9 % (utež 0,3, brez Flasha).' },
  { id: 'arctic', label: 'arctic-embed-l-v2.0', description: 'Odprt model, teče lokalno, ves korpus (posnetek 29. 9. 2026). Čisti vektor sidro@3 98,4 %; golden v4 R@5 pri uteži 0,9: 89,9 % (MiniLM 53,9 %, brez Flasha).' },
];

export const LAB_WEIGHT_OPTIONS: { id: LabKnnWeight; label: string; description: string }[] = [
  { id: 'privzeto', label: 'Privzeto', description: 'Utež, ki je nastavljena na instanci za izbrani model (MiniLM 0,3, arctic 0,7).' },
  { id: '0.3', label: '0,3', description: 'BM25 70 %, vektor 30 %.' },
  { id: '0.5', label: '0,5', description: 'BM25 in vektor enako.' },
  { id: '0.7', label: '0,7', description: 'BM25 30 %, vektor 70 %. Pri arcticu sidro@3 99,5 %, golden v4 R@5 87,2 %.' },
  { id: '0.9', label: '0,9', description: 'BM25 10 %, vektor 90 %. Pri arcticu golden v4 R@5 89,9 % (izbrana na umeritvenem delu).' },
];

export const LAB_FLASH_OPTIONS: { id: LabFlash; label: string; description: string }[] = [
  { id: 'privzeto', label: 'Privzeto', description: 'Nastavitev instance (pri arcticu najboljši košček, pri MiniLM začetek člena).' },
  { id: 'zacetek', label: 'Flash: začetek člena', description: 'Prvih 1200 znakov.' },
  { id: 'kosc', label: 'Flash: najboljši košček', description: 'Odstavek, ki ga je izbral vektor. Smiselno z arcticom (sidro@1 92,5 %).' },
  { id: 'off', label: 'Brez Flasha', description: 'Vrstni red iskanja ostane. Brez oblaka. Pri arcticu sidro@3 99,5 %.' },
];

const has = <T extends string>(opts: { id: T }[], v: unknown): v is T => typeof v === 'string' && opts.some((o) => o.id === v);

export function resolveSearchLab(x: unknown): SearchLabChoice {
  const o = (x ?? {}) as Record<string, unknown>;
  return {
    embed: has(LAB_EMBED_OPTIONS, o.embed) ? o.embed : 'privzeto',
    weight: has(LAB_WEIGHT_OPTIONS, o.weight) ? o.weight : 'privzeto',
    flash: has(LAB_FLASH_OPTIONS, o.flash) ? o.flash : 'privzeto',
  };
}

export function isSearchLabDefault(c: SearchLabChoice): boolean {
  return c.embed === 'privzeto' && c.weight === 'privzeto' && c.flash === 'privzeto';
}
